import React, { useState, useEffect } from 'react';
import { Bell, Mail, Clock, Zap, Save, Plus, Trash2, Calendar, AlertCircle, CheckCircle, Settings, Toggle, X, ChevronRight, Sparkles, Database, Download, RefreshCw, Edit, Loader2, Columns, BarChart2, Palette, Globe, Shield, Key, Eye, Copy } from 'lucide-react';
import { useUser } from '@/context/UserContext';
import { TestDataReset } from '@/components/TestDataReset';
import { useDataManagement } from '@/hooks/useDataManagement';
import { EmailTemplateEditor } from '@/components/EmailTemplateEditor';
import { useAutomation } from '@/hooks/useAutomation';
import ColumnSettingsManager from '@/components/ColumnSettingsManager';
import { useOfferStats } from '@/hooks/useOfferStats';
import { useToast } from '@/components/ui/Toast';
import { useSettings } from '@/hooks/useSettings';

interface RuleFormData {
  name: string;
  description: string;
  type: 'offer_expiry' | 'offer_status' | 'client_activity' | 'scheduled';
  trigger: any;
  action: any;
}

export function AutomationSettings() {
  const { isAdmin } = useUser();
  const { showToast } = useToast();
  const { resetStats, refreshStats } = useOfferStats();
  const { 
    generalSettings, 
    securitySettings, 
    saveGeneralSettings, 
    saveSecuritySettings, 
    generateApiKey 
  } = useSettings();
  const [activeTab, setActiveTab] = useState<'general' | 'rules' | 'notifications' | 'templates' | 'columns' | 'security' | 'admin'>('general');
  
  // Hook do automatyzacji
  const {
    rules,
    notificationSettings,
    emailTemplates,
    loading: automationLoading,
    error: automationError,
    saveRule,
    toggleRule,
    deleteRule,
    saveNotificationSettings: saveNotificationSettingsToDb,
    saveEmailTemplate,
    refreshData
  } = useAutomation();

  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<any>(null);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Formularz reguły
  const [ruleForm, setRuleForm] = useState<RuleFormData>({
    name: '',
    description: '',
    type: 'offer_expiry',
    trigger: {},
    action: { type: 'email' }
  });

  // Lokalne kopie ustawień do edycji
  const [localGeneralSettings, setLocalGeneralSettings] = useState(generalSettings);
  const [localSecuritySettings, setLocalSecuritySettings] = useState(securitySettings);

  // Synchronizuj lokalne ustawienia z hookami
  useEffect(() => {
    setLocalGeneralSettings(generalSettings);
  }, [generalSettings]);

  useEffect(() => {
    setLocalSecuritySettings(securitySettings);
  }, [securitySettings]);

  // Lokalne ustawienia powiadomień
  const [localNotificationSettings, setLocalNotificationSettings] = useState(notificationSettings);

  // Synchronizuj lokalne ustawienia z hookiem
  useEffect(() => {
    setLocalNotificationSettings(notificationSettings);
  }, [notificationSettings]);

  // Obsługa przełączania reguły
  const handleToggleRule = async (ruleId: string) => {
    const result = await toggleRule(ruleId);
    if (!result.success) {
      alert('Błąd podczas przełączania reguły');
    }
  };

  // Obsługa usuwania reguły
  const handleDeleteRule = async (ruleId: string) => {
    if (confirm('Czy na pewno chcesz usunąć tę regułę?')) {
      const result = await deleteRule(ruleId);
      if (!result.success) {
        alert('Błąd podczas usuwania reguły');
      }
    }
  };

  // Obsługa zapisywania reguły
  const handleSaveRule = async () => {
    setSaving(true);
    try {
      const ruleData = {
        ...ruleForm,
        id: editingRule?.id,
        trigger: generateTriggerData(ruleForm.type, ruleForm.trigger),
        action: generateActionData(ruleForm.action)
      };

      const result = await saveRule(ruleData);
      if (result.success) {
        setShowRuleModal(false);
        setEditingRule(null);
        resetRuleForm();
      } else {
        alert('Błąd podczas zapisywania reguły');
      }
    } finally {
      setSaving(false);
    }
  };

  // Generuj dane wyzwalacza na podstawie typu
  const generateTriggerData = (type: string, formData: any) => {
    switch (type) {
      case 'offer_expiry':
        return { days: parseInt(formData.days) || 3 };
      case 'offer_status':
        return { status: formData.status || 'accepted' };
      case 'scheduled':
        return { 
          time: formData.time || '09:00',
          frequency: formData.frequency || 'daily'
        };
      default:
        return {};
    }
  };

  // Generuj dane akcji
  const generateActionData = (formData: any) => {
    return {
      type: formData.type || 'email',
      template: formData.template,
      recipients: formData.recipients || []
    };
  };

  // Resetuj formularz
  const resetRuleForm = () => {
    setRuleForm({
      name: '',
      description: '',
      type: 'offer_expiry',
      trigger: {},
      action: { type: 'email' }
    });
  };

  // Obsługa zapisywania ustawień powiadomień
  const handleSaveNotificationSettings = async () => {
    setSaving(true);
    try {
      const result = await saveNotificationSettingsToDb(localNotificationSettings);
      if (result.success) {
        alert('Ustawienia zostały zapisane!');
      } else {
        alert('Błąd podczas zapisywania ustawień');
      }
    } finally {
      setSaving(false);
    }
  };

  // Obsługa zapisywania szablonu
  const handleSaveTemplate = async (template: any) => {
    const result = await saveEmailTemplate({
      ...template,
      id: template.id || undefined,
      code: template.code || template.id,
      variables: template.variables || []
    });
    
    if (result.success) {
      setShowTemplateEditor(false);
      setEditingTemplate(null);
      alert('Szablon został zaktualizowany!');
      await refreshData();
    } else {
      alert('Błąd podczas zapisywania szablonu');
    }
  };

  // Przenieś hooki na poziom komponentu (dla administracji)
  const { loading, progress, performCleanup, createBackup } = useDataManagement();
  const [cleanupOptions, setCleanupOptions] = useState({
    offers: false,
    statistics: false,
    logs: false,
    calculations: false,
    cache: false,
    payroll: false
  });
  const [offerDateRange, setOfferDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Renderuj ogólne ustawienia
  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white">Ustawienia ogólne</h3>
        <p className="text-sm text-gray-400 mt-1">Dostosuj podstawowe ustawienia aplikacji</p>
      </div>

      {/* Wygląd i język */}
      <div className="bg-zinc-800/50 backdrop-blur-sm rounded-xl border border-zinc-700 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Palette className="w-5 h-5 text-orange-400" />
          <h4 className="font-medium text-white">Wygląd i język</h4>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Motyw</label>
            <select 
              value={localGeneralSettings.theme}
              onChange={(e) => setLocalGeneralSettings({ ...localGeneralSettings, theme: e.target.value as any })}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="light">Jasny</option>
              <option value="dark">Ciemny</option>
              <option value="auto">Automatyczny</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Język</label>
            <select 
              value={localGeneralSettings.language}
              onChange={(e) => setLocalGeneralSettings({ ...localGeneralSettings, language: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="pl">Polski</option>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={localGeneralSettings.animationsEnabled}
                onChange={(e) => setLocalGeneralSettings({ ...localGeneralSettings, animationsEnabled: e.target.checked })}
                className="w-4 h-4 text-orange-500 bg-zinc-700 border-zinc-600 rounded"
              />
              <span className="text-white">Włącz animacje</span>
            </label>
          </div>
        </div>
      </div>

      {/* Format danych */}
      <div className="bg-zinc-800/50 backdrop-blur-sm rounded-xl border border-zinc-700 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Globe className="w-5 h-5 text-blue-400" />
          <h4 className="font-medium text-white">Format danych</h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Format daty</label>
            <select 
              value={localGeneralSettings.dateFormat}
              onChange={(e) => setLocalGeneralSettings({ ...localGeneralSettings, dateFormat: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="DD.MM.YYYY">DD.MM.YYYY</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Format czasu</label>
            <select 
              value={localGeneralSettings.timeFormat}
              onChange={(e) => setLocalGeneralSettings({ ...localGeneralSettings, timeFormat: e.target.value as any })}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="24h">24-godzinny</option>
              <option value="12h">12-godzinny (AM/PM)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Waluta</label>
            <select 
              value={localGeneralSettings.currency}
              onChange={(e) => setLocalGeneralSettings({ ...localGeneralSettings, currency: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="PLN">PLN (zł)</option>
              <option value="EUR">EUR (€)</option>
              <option value="USD">USD ($)</option>
              <option value="GBP">GBP (£)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Pierwszy dzień tygodnia</label>
            <select 
              value={localGeneralSettings.firstDayOfWeek}
              onChange={(e) => setLocalGeneralSettings({ ...localGeneralSettings, firstDayOfWeek: Number(e.target.value) })}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="1">Poniedziałek</option>
              <option value="0">Niedziela</option>
            </select>
          </div>
        </div>
      </div>

      <button
        onClick={async () => {
          const result = await saveGeneralSettings(localGeneralSettings);
          if (result.success) {
            showToast({
              type: 'success',
              title: 'Zapisano ustawienia',
              message: 'Ustawienia ogólne zostały zaktualizowane'
            });
          } else {
            showToast({
              type: 'error',
              title: 'Błąd',
              message: 'Nie udało się zapisać ustawień'
            });
          }
        }}
        className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all hover:scale-105 flex items-center gap-2"
      >
        <Save className="w-4 h-4" />
        Zapisz ustawienia
      </button>
    </div>
  );

  // Renderuj ustawienia bezpieczeństwa
  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white">Ustawienia bezpieczeństwa</h3>
        <p className="text-sm text-gray-400 mt-1">Zarządzaj bezpieczeństwem konta i aplikacji</p>
      </div>

      {/* Uwierzytelnianie */}
      <div className="bg-zinc-800/50 backdrop-blur-sm rounded-xl border border-zinc-700 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-5 h-5 text-red-400" />
          <h4 className="font-medium text-white">Uwierzytelnianie</h4>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-white">Weryfikacja dwuetapowa</p>
              <p className="text-sm text-gray-400">Dodatkowe zabezpieczenie konta</p>
            </div>
            <button
              onClick={() => setLocalSecuritySettings({ ...localSecuritySettings, twoFactorEnabled: !localSecuritySettings.twoFactorEnabled })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                localSecuritySettings.twoFactorEnabled ? 'bg-orange-500' : 'bg-zinc-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  localSecuritySettings.twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Timeout sesji (minuty)
            </label>
            <input
              type="number"
              value={localSecuritySettings.sessionTimeout}
              onChange={(e) => setLocalSecuritySettings({ ...localSecuritySettings, sessionTimeout: Number(e.target.value) })}
              min="5"
              max="480"
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>
      </div>

      {/* API i integracje */}
      <div className="bg-zinc-800/50 backdrop-blur-sm rounded-xl border border-zinc-700 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Key className="w-5 h-5 text-yellow-400" />
          <h4 className="font-medium text-white">API i integracje</h4>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-white">Dostęp przez API</p>
              <p className="text-sm text-gray-400">Pozwól na integracje zewnętrzne</p>
            </div>
            <button
              onClick={() => setLocalSecuritySettings({ ...localSecuritySettings, apiAccess: !localSecuritySettings.apiAccess })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                localSecuritySettings.apiAccess ? 'bg-orange-500' : 'bg-zinc-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  localSecuritySettings.apiAccess ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {localSecuritySettings.apiAccess && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Klucz API
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={localSecuritySettings.apiKey || 'Wygeneruj nowy klucz...'}
                    readOnly
                    className="flex-1 px-3 py-2 bg-zinc-700 border border-zinc-600 text-gray-400 rounded-lg"
                  />
                  <button
                    onClick={() => {
                      const newKey = generateApiKey();
                      setLocalSecuritySettings({ ...localSecuritySettings, apiKey: newKey });
                      showToast({
                        type: 'success',
                        title: 'Wygenerowano klucz',
                        message: 'Nowy klucz API został wygenerowany'
                      });
                    }}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                  >
                    Generuj
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(localSecuritySettings.apiKey);
                      showToast({
                        type: 'success',
                        title: 'Skopiowano',
                        message: 'Klucz API został skopiowany'
                      });
                    }}
                    className="p-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <button
        onClick={async () => {
          const result = await saveSecuritySettings(localSecuritySettings);
          if (result.success) {
            showToast({
              type: 'success',
              title: 'Zapisano ustawienia',
              message: 'Ustawienia bezpieczeństwa zostały zaktualizowane'
            });
          } else {
            showToast({
              type: 'error',
              title: 'Błąd',
              message: 'Nie udało się zapisać ustawień'
            });
          }
        }}
        className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all hover:scale-105 flex items-center gap-2"
      >
        <Save className="w-4 h-4" />
        Zapisz ustawienia
      </button>
    </div>
  );

  const renderRules = () => (
    <div className="space-y-6">
      {/* Nagłówek z przyciskiem dodawania */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Reguły automatyzacji</h3>
          <p className="text-sm text-gray-400 mt-1">Zarządzaj automatycznymi akcjami w systemie</p>
        </div>
        <button
          onClick={() => {
            resetRuleForm();
            setEditingRule(null);
            setShowRuleModal(true);
          }}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all hover:scale-105 text-sm sm:text-base"
        >
          <Plus className="w-4 h-4" />
          Nowa reguła
        </button>
      </div>

      {/* Ładowanie */}
      {automationLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      )}

      {/* Błąd */}
      {automationError && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-400">Błąd ładowania reguł: {automationError}</p>
        </div>
      )}

      {/* Lista reguł */}
      {!automationLoading && !automationError && (
        <div className="space-y-4">
          {rules.length === 0 ? (
            <div className="text-center py-12 bg-zinc-800/50 rounded-xl border border-zinc-700">
              <Zap className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Brak reguł automatyzacji</p>
              <p className="text-sm text-gray-500 mt-2">Kliknij "Nowa reguła" aby dodać pierwszą</p>
            </div>
          ) : (
            rules.map((rule) => (
              <div
                key={rule.id}
                className={`bg-zinc-800/50 backdrop-blur-sm rounded-xl border p-6 transition-all ${
                  rule.is_active ? 'border-orange-500/30 shadow-lg shadow-orange-500/10' : 'border-zinc-700 opacity-75'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        rule.is_active ? 'bg-orange-500/10' : 'bg-zinc-700'
                      }`}>
                        {rule.type === 'offer_expiry' && <Clock className="w-5 h-5 text-orange-400" />}
                        {rule.type === 'offer_status' && <Bell className="w-5 h-5 text-blue-400" />}
                        {rule.type === 'scheduled' && <Calendar className="w-5 h-5 text-purple-400" />}
                        {rule.type === 'client_activity' && <Zap className="w-5 h-5 text-yellow-400" />}
                      </div>
                      <div>
                        <h4 className="font-semibold text-white flex items-center gap-2">
                          {rule.name}
                          {rule.is_active && (
                            <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full animate-pulse">
                              Aktywna
                            </span>
                          )}
                        </h4>
                        <p className="text-sm text-gray-400 mt-1">{rule.description}</p>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-400">
                          Wyzwalacz: 
                          {rule.trigger?.days && ` ${rule.trigger.days} dni przed`}
                          {rule.trigger?.status && ` Status: ${rule.trigger.status}`}
                          {rule.trigger?.time && ` Codziennie o ${rule.trigger.time}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-400">
                          Akcja: {rule.action?.type === 'email' ? 'Email' : rule.action?.type === 'notification' ? 'Powiadomienie' : 'Zadanie'}
                        </span>
                      </div>
                      {rule.last_triggered && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-500">
                            Ostatnie uruchomienie: {new Date(rule.last_triggered).toLocaleDateString('pl-PL')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleToggleRule(rule.id)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        rule.is_active ? 'bg-orange-500' : 'bg-zinc-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          rule.is_active ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <button
                      onClick={() => {
                        setEditingRule(rule);
                        setRuleForm({
                          name: rule.name,
                          description: rule.description,
                          type: rule.type,
                          trigger: rule.trigger,
                          action: rule.action
                        });
                        setShowRuleModal(true);
                      }}
                      className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
                    >
                      <Settings className="w-4 h-4 text-gray-400" />
                    </button>
                    <button
                      onClick={() => handleDeleteRule(rule.id)}
                      className="p-2 hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Statystyki */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-6 sm:mt-8">
        <div className="bg-zinc-800/50 backdrop-blur-sm p-4 rounded-xl border border-zinc-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-400 font-medium">Aktywne reguły</p>
              <p className="text-2xl font-bold text-white mt-1">
                {rules.filter(r => r.is_active).length} / {rules.length}
              </p>
            </div>
            <div className="p-3 bg-orange-500/10 rounded-full">
              <Zap className="w-6 h-6 text-orange-400" />
            </div>
          </div>
        </div>

        <div className="bg-zinc-800/50 backdrop-blur-sm p-4 rounded-xl border border-zinc-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-400 font-medium">Wykonane dzisiaj</p>
              <p className="text-2xl font-bold text-white mt-1">
                {rules.reduce((sum, r) => sum + (r.execution_count || 0), 0)}
              </p>
            </div>
            <div className="p-3 bg-green-500/10 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-zinc-800/50 backdrop-blur-sm p-4 rounded-xl border border-zinc-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-400 font-medium">Szablony email</p>
              <p className="text-2xl font-bold text-white mt-1">{emailTemplates.length}</p>
            </div>
            <div className="p-3 bg-blue-500/10 rounded-full">
              <Mail className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotifications = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white">Ustawienia powiadomień</h3>
        <p className="text-sm text-gray-400 mt-1">Wybierz jak chcesz otrzymywać powiadomienia</p>
      </div>

      {/* Kanały powiadomień */}
      <div className="bg-zinc-800/50 backdrop-blur-sm rounded-xl border border-zinc-700 p-6 space-y-6">
        <h4 className="font-medium text-white">Kanały powiadomień</h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-white">Email</p>
                <p className="text-sm text-gray-400">Otrzymuj powiadomienia na adres email</p>
              </div>
            </div>
            <button
              onClick={() => setLocalNotificationSettings({ ...localNotificationSettings, email: !localNotificationSettings.email })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                localNotificationSettings.email ? 'bg-orange-500' : 'bg-zinc-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  localNotificationSettings.email ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-white">Powiadomienia push</p>
                <p className="text-sm text-gray-400">Powiadomienia w przeglądarce</p>
              </div>
            </div>
            <button
              onClick={() => setLocalNotificationSettings({ ...localNotificationSettings, push: !localNotificationSettings.push })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                localNotificationSettings.push ? 'bg-orange-500' : 'bg-zinc-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  localNotificationSettings.push ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-white">Powiadomienia w aplikacji</p>
                <p className="text-sm text-gray-400">Pokazuj powiadomienia w systemie</p>
              </div>
            </div>
            <button
              onClick={() => setLocalNotificationSettings({ ...localNotificationSettings, inApp: !localNotificationSettings.inApp })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                localNotificationSettings.inApp ? 'bg-orange-500' : 'bg-zinc-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  localNotificationSettings.inApp ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Częstotliwość */}
      <div className="bg-zinc-800/50 backdrop-blur-sm rounded-xl border border-zinc-700 p-6">
        <h4 className="font-medium text-white mb-4">Częstotliwość powiadomień</h4>
        <div className="space-y-3">
          {(['instant', 'daily', 'weekly'] as const).map((frequency) => (
            <label key={frequency} className="flex items-center gap-3 cursor-pointer group">
              <input
                type="radio"
                name="digest"
                value={frequency}
                checked={localNotificationSettings.digest === frequency}
                onChange={(e) => setLocalNotificationSettings({ ...localNotificationSettings, digest: e.target.value as any })}
                className="w-4 h-4 text-orange-500 bg-zinc-700 border-zinc-600 focus:ring-orange-500"
              />
              <div>
                <p className="font-medium text-white group-hover:text-orange-400 transition-colors">
                  {frequency === 'instant' && 'Natychmiast'}
                  {frequency === 'daily' && 'Podsumowanie dzienne'}
                  {frequency === 'weekly' && 'Podsumowanie tygodniowe'}
                </p>
                <p className="text-sm text-gray-400">
                  {frequency === 'instant' && 'Otrzymuj powiadomienia od razu'}
                  {frequency === 'daily' && 'Jedno podsumowanie każdego dnia o 9:00'}
                  {frequency === 'weekly' && 'Jedno podsumowanie w poniedziałki o 9:00'}
                </p>
              </div>
            </label>
          ))}
        </div>
      </div>

      <button
        onClick={handleSaveNotificationSettings}
        disabled={saving}
        className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all hover:scale-105 flex items-center gap-2 disabled:opacity-50"
      >
        {saving ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Zapisywanie...
          </>
        ) : (
          <>
            <Save className="w-4 h-4" />
            Zapisz ustawienia
          </>
        )}
      </button>
    </div>
  );

  const renderTemplates = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white">Szablony wiadomości</h3>
        <p className="text-sm text-gray-400 mt-1">Zarządzaj szablonami emaili i powiadomień</p>
      </div>

      {automationLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      )}

      {!automationLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {emailTemplates.map((template) => (
            <div key={template.id} className="bg-zinc-800/50 backdrop-blur-sm rounded-xl border border-zinc-700 p-6 hover:border-orange-500/30 transition-all cursor-pointer group">
              <h4 className="font-semibold text-white group-hover:text-orange-400 transition-colors">{template.name}</h4>
              <p className="text-sm text-gray-400 mt-2">ID: {template.code}</p>
              <div className="mt-4">
                <p className="text-xs text-gray-500 font-medium mb-2">Dostępne zmienne:</p>
                <div className="flex flex-wrap gap-2">
                  {template.variables.map((variable) => (
                    <span key={variable} className="px-2 py-1 bg-zinc-700 text-orange-400 rounded text-xs font-mono">
                      {`{{${variable}}}`}
                    </span>
                  ))}
                </div>
              </div>
              <button 
                onClick={() => {
                  setEditingTemplate(template);
                  setShowTemplateEditor(true);
                }}
                className="mt-4 text-sm text-orange-400 hover:text-orange-300 font-medium transition-colors flex items-center gap-1"
              >
                <Edit className="w-3 h-3" />
                Edytuj szablon 
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderAdminSettings = () => {
    const handleCleanup = async () => {
      if (!confirm('Czy na pewno chcesz usunąć wybrane dane? Ta operacja jest nieodwracalna!')) {
        return;
      }

      try {
        const options: any = {};
        
        if (cleanupOptions.offers) {
          options.offers = {
            startDate: offerDateRange.startDate || undefined,
            endDate: offerDateRange.endDate || undefined
          };
        }
        
        Object.keys(cleanupOptions).forEach(key => {
          if (key !== 'offers' && cleanupOptions[key as keyof typeof cleanupOptions]) {
            options[key] = true;
          }
        });

        const result = await performCleanup(options);
        if (result.success) {
          alert(result.message);
          // Reset opcji
          setCleanupOptions({
            offers: false,
            statistics: false,
            logs: false,
            calculations: false,
            cache: false,
            payroll: false
          });
        }
      } catch (error) {
        alert('Błąd podczas czyszczenia danych');
      }
    };

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-white">Ustawienia administratora</h3>
          <p className="text-sm text-gray-400 mt-1">Zarządzaj ustawieniami systemu i danymi</p>
        </div>

        {/* Resetowanie statystyk */}
        <div className="bg-zinc-800 rounded-lg p-6 border border-zinc-700 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <BarChart2 className="w-6 h-6 text-blue-500" />
            <h4 className="text-lg font-semibold text-white">Statystyki ofert</h4>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Wyzeruj liczniki ofert wyświetlane w prawym górnym rogu aplikacji.
          </p>
          <button
            onClick={async () => {
              if (confirm('Czy na pewno chcesz wyzerować statystyki ofert? To usunie wszystkie oferty z systemu!')) {
                const result = await resetStats();
                if (result.success) {
                  showToast({
                    type: 'success',
                    title: 'Statystyki wyzerowane',
                    message: 'Liczniki ofert zostały zresetowane'
                  });
                  await refreshStats();
                } else {
                  showToast({
                    type: 'error',
                    title: 'Błąd',
                    message: 'Nie udało się wyzerować statystyk'
                  });
                }
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Wyzeruj statystyki
          </button>
        </div>

        {/* Zarządzanie danymi */}
        <div className="bg-zinc-800 rounded-lg p-6 border border-zinc-700">
          <div className="flex items-center gap-3 mb-6">
            <Database className="w-6 h-6 text-orange-500" />
            <h4 className="text-lg font-semibold text-white">Zarządzanie danymi</h4>
          </div>

          {/* Backup */}
          <div className="mb-6 p-4 bg-zinc-900 rounded-lg border border-zinc-600">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="font-medium text-white">Backup danych</h5>
                <p className="text-sm text-gray-400 mt-1">Utwórz kopię zapasową wszystkich danych</p>
              </div>
              <button
                onClick={createBackup}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                <Download className="w-4 h-4" />
                Utwórz backup
              </button>
            </div>
          </div>

          {/* Opcje czyszczenia */}
          <div className="space-y-4">
            <h5 className="font-medium text-white">Czyszczenie danych</h5>
            
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cleanupOptions.offers}
                  onChange={(e) => setCleanupOptions({ ...cleanupOptions, offers: e.target.checked })}
                  className="w-4 h-4 text-orange-500 bg-zinc-700 border-zinc-600 rounded focus:ring-orange-500"
                />
                <div className="flex-1">
                  <span className="text-white">Historia ofert</span>
                  <p className="text-xs text-gray-400">Usuń oferty z wybranego zakresu dat</p>
                </div>
              </label>
              
              {cleanupOptions.offers && (
                <div className="ml-7 p-3 bg-zinc-900 rounded-lg space-y-2">
                  <div>
                    <label className="text-xs text-gray-400">Od daty:</label>
                    <input
                      type="date"
                      value={offerDateRange.startDate}
                      onChange={(e) => setOfferDateRange({ ...offerDateRange, startDate: e.target.value })}
                      className="w-full mt-1 px-3 py-1 bg-zinc-800 border border-zinc-700 text-white rounded focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Do daty:</label>
                    <input
                      type="date"
                      value={offerDateRange.endDate}
                      onChange={(e) => setOfferDateRange({ ...offerDateRange, endDate: e.target.value })}
                      className="w-full mt-1 px-3 py-1 bg-zinc-800 border border-zinc-700 text-white rounded focus:border-orange-500"
                    />
                  </div>
                </div>
              )}

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cleanupOptions.statistics}
                  onChange={(e) => setCleanupOptions({ ...cleanupOptions, statistics: e.target.checked })}
                  className="w-4 h-4 text-orange-500 bg-zinc-700 border-zinc-600 rounded focus:ring-orange-500"
                />
                <div className="flex-1">
                  <span className="text-white">Statystyki sprzedaży</span>
                  <p className="text-xs text-gray-400">Usuń wszystkie statystyki i wykresy</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cleanupOptions.logs}
                  onChange={(e) => setCleanupOptions({ ...cleanupOptions, logs: e.target.checked })}
                  className="w-4 h-4 text-orange-500 bg-zinc-700 border-zinc-600 rounded focus:ring-orange-500"
                />
                <div className="flex-1">
                  <span className="text-white">Logi systemowe</span>
                  <p className="text-xs text-gray-400">Usuń historię zdarzeń systemowych</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cleanupOptions.calculations}
                  onChange={(e) => setCleanupOptions({ ...cleanupOptions, calculations: e.target.checked })}
                  className="w-4 h-4 text-orange-500 bg-zinc-700 border-zinc-600 rounded focus:ring-orange-500"
                />
                <div className="flex-1">
                  <span className="text-white">Zapisane kalkulacje</span>
                  <p className="text-xs text-gray-400">Usuń historię kalkulacji produktów</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cleanupOptions.cache}
                  onChange={(e) => setCleanupOptions({ ...cleanupOptions, cache: e.target.checked })}
                  className="w-4 h-4 text-orange-500 bg-zinc-700 border-zinc-600 rounded focus:ring-orange-500"
                />
                <div className="flex-1">
                  <span className="text-white">Cache i dane tymczasowe</span>
                  <p className="text-xs text-gray-400">Wyczyść pamięć podręczną przeglądarki</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cleanupOptions.payroll}
                  onChange={(e) => setCleanupOptions({ ...cleanupOptions, payroll: e.target.checked })}
                  className="w-4 h-4 text-orange-500 bg-zinc-700 border-zinc-600 rounded focus:ring-orange-500"
                />
                <div className="flex-1">
                  <span className="text-white">Rozliczenia pracowników</span>
                  <p className="text-xs text-gray-400">Usuń historię rozliczeń i kalkulacje wynagrodzeń</p>
                </div>
              </label>
            </div>

            {/* Przycisk czyszczenia */}
            <div className="mt-6 flex items-center gap-4">
              <button
                onClick={handleCleanup}
                disabled={loading || !Object.values(cleanupOptions).some(v => v)}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Czyszczenie... {Math.round(progress)}%
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Wyczyść wybrane dane
                  </>
                )}
              </button>
              
              {loading && (
                <div className="flex-1">
                  <div className="w-full bg-zinc-700 rounded-full h-2">
                    <div 
                      className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Resetowanie danych testowych */}
        <TestDataReset />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900">
      {/* Nagłówek */}
      <div className="bg-zinc-800/50 backdrop-blur-lg shadow-xl border-b border-zinc-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <Settings className="w-8 h-8 text-orange-500" />
                </div>
                Automatyzacja i ustawienia
              </h1>
              <p className="mt-1 text-sm text-gray-400">
                Skonfiguruj automatyczne akcje i powiadomienia
              </p>
            </div>
          </div>

          {/* Zakładki */}
          <div className="flex space-x-2 sm:space-x-4 lg:space-x-8 border-b border-zinc-700 -mb-px overflow-x-auto scrollbar-hide">
            {[
              { id: 'general', label: 'Ogólne', icon: Palette },
              { id: 'security', label: 'Bezpieczeństwo', icon: Shield },
              { id: 'rules', label: 'Reguły', icon: Zap },
              { id: 'notifications', label: 'Powiadomienia', icon: Bell },
              { id: 'templates', label: 'Szablony', icon: Mail },
              { id: 'columns', label: 'Kolumny', icon: Columns },
              ...(isAdmin ? [{ id: 'admin', label: 'Administracja', icon: Settings }] : [])
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-3 sm:py-4 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm transition-all flex items-center gap-1 sm:gap-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-400'
                    : 'border-transparent text-gray-400 hover:text-white hover:border-gray-600'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Zawartość */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {activeTab === 'general' && renderGeneralSettings()}
        {activeTab === 'security' && renderSecuritySettings()}
        {activeTab === 'rules' && renderRules()}
        {activeTab === 'notifications' && renderNotifications()}
        {activeTab === 'templates' && renderTemplates()}
        {activeTab === 'columns' && <ColumnSettingsManager />}
        {activeTab === 'admin' && isAdmin && renderAdminSettings()}
      </div>

      {/* Modal edytora szablonów */}
      {showTemplateEditor && editingTemplate && (
        <div className="fixed inset-0 z-50 px-4">
          <EmailTemplateEditor
            template={editingTemplate}
            onSave={handleSaveTemplate}
            onClose={() => {
              setShowTemplateEditor(false);
              setEditingTemplate(null);
            }}
          />
        </div>
      )}

      {/* Modal dodawania/edycji reguły */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-800 rounded-xl max-w-md w-full p-4 sm:p-6 animate-scaleIn border border-zinc-700 shadow-2xl">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-lg font-semibold text-white">
                {editingRule ? 'Edytuj regułę' : 'Nowa reguła automatyzacji'}
              </h3>
              <button
                onClick={() => {
                  setShowRuleModal(false);
                  setEditingRule(null);
                  resetRuleForm();
                }}
                className="p-1 hover:bg-zinc-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Formularz reguły */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nazwa reguły</label>
                <input
                  type="text"
                  value={ruleForm.name}
                  onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                  placeholder="np. Przypomnienie o fakturze"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Opis</label>
                <input
                  type="text"
                  value={ruleForm.description}
                  onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                  placeholder="Krótki opis działania reguły"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Typ wyzwalacza</label>
                <select 
                  value={ruleForm.type}
                  onChange={(e) => setRuleForm({ ...ruleForm, type: e.target.value as any })}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                >
                  <option value="offer_expiry">Wygaśnięcie oferty</option>
                  <option value="offer_status">Zmiana statusu oferty</option>
                  <option value="scheduled">Harmonogram</option>
                  <option value="client_activity">Aktywność klienta</option>
                </select>
              </div>

              {/* Dynamiczne pola w zależności od typu */}
              {ruleForm.type === 'offer_expiry' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Dni przed wygaśnięciem</label>
                  <input
                    type="number"
                    value={ruleForm.trigger.days || 3}
                    onChange={(e) => setRuleForm({ ...ruleForm, trigger: { ...ruleForm.trigger, days: e.target.value }})}
                    className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                    min="1"
                  />
                </div>
              )}

              {ruleForm.type === 'scheduled' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Czas</label>
                    <input
                      type="time"
                      value={ruleForm.trigger.time || '09:00'}
                      onChange={(e) => setRuleForm({ ...ruleForm, trigger: { ...ruleForm.trigger, time: e.target.value }})}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Częstotliwość</label>
                    <select
                      value={ruleForm.trigger.frequency || 'daily'}
                      onChange={(e) => setRuleForm({ ...ruleForm, trigger: { ...ruleForm.trigger, frequency: e.target.value }})}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                    >
                      <option value="daily">Codziennie</option>
                      <option value="weekly">Co tydzień</option>
                      <option value="monthly">Co miesiąc</option>
                    </select>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Akcja</label>
                <select 
                  value={ruleForm.action.type}
                  onChange={(e) => setRuleForm({ ...ruleForm, action: { ...ruleForm.action, type: e.target.value }})}
                  className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                >
                  <option value="email">Wyślij email</option>
                  <option value="notification">Pokaż powiadomienie</option>
                  <option value="task">Utwórz zadanie</option>
                </select>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowRuleModal(false);
                    setEditingRule(null);
                    resetRuleForm();
                  }}
                  className="flex-1 px-4 py-2 border border-zinc-600 text-gray-300 rounded-lg hover:bg-zinc-700 transition-all"
                >
                  Anuluj
                </button>
                <button 
                  onClick={handleSaveRule}
                  disabled={saving || !ruleForm.name || !ruleForm.description}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all hover:scale-105 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Zapisywanie...
                    </>
                  ) : (
                    editingRule ? 'Zapisz zmiany' : 'Dodaj regułę'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
